<template lang="pug">
  .product-item
    v-card(flat @click="clickEdit")
      v-row(no-gutters)
        v-col.shrink(style="margin-top: 2px;")
          y-checkbox.ma-0.pa-0.mr-2(v-model="product.selected" @click.once="")
        v-col.py-3.pl-3.pr-2.shrink
          v-img(:src="product.image ? product.image.file : require('@/assets/image_placeholder.png')" width="88px" aspect-ratio="1")
            div.d-flex.justify-center.align-end(style="height: 100%" v-if="product.inventory_enabled")
              v-chip.white--text.mb-2(x-small color="primary500") {{ getProductCount() }}
        v-col.py-3
          div.p1.basic800--text.mb-1 {{ product.name }}
          div.c1.basic600--text.product-description.mb-1(v-html="product.description")
          div.c2.basic600--text {{ product.price | displayPrice }}
        v-col.shrink.py-3.pr-1(@click.stop v-if="member.role === 'Admin'")
          div
            y-switch(:key="switchKey" v-model="status")
          div.d-flex.justify-end.align-end
            v-menu(left :offset-x='true')
              template(v-slot:activator="{ on, attrs }")
                v-btn.mt-1(icon v-on="on" color="transparent" small)
                  v-icon(color="basic700") mdi-dots-vertical
              v-list
                v-list-item(@click="clickEdit")
                  v-list-item-title.s2.basic800--text {{ $t('edit') }}
                v-list-item(@click="showDuplicateProductDialog = true")
                  v-list-item-title.s2.basic800--text {{ $t('duplicate_product') }}
                v-list-item(@click="clickDelete")
                  v-list-item-title.s2.basic800--text {{ $t('delete_product') }}

    .modals
      add-product(v-if="showAddProductDialog" :open.sync="showAddProductDialog" :parentProduct.sync="product" :setweight="setweight" :setdimension="setdimension")
      duplicate-product(v-if="showDuplicateProductDialog" :open.sync="showDuplicateProductDialog" :parentProduct.sync="product" @duplicated="$emit('duplicated', $event)")
      y-dialog(v-if="openDeleteDialog" v-model='openDeleteDialog', max-width='320')
        v-card
          v-card-title.h6.basic800--text.px-4 {{ $t('delete_product') }}
          v-card-text.p2.primary500--text.px-4
            div {{ $t('delete_product_confirmation') }}
          v-card-actions
            v-spacer
            v-btn(color='white', text @click='openDeleteDialog = false')
              .button-Aa-small.primary400--text {{ $t('cancel') }}
            v-btn.mr-2(color='danger500' depressed height=32 @click='startDelete' :loading="isLoading" dark)
              .button-AA-small.basic100--text {{ $t('delete') }}
      y-dialog(v-if="openWarningDialog" v-model='openWarningDialog', max-width='320' persistent)
        v-card
          v-card-title.s1.danger500--text.px-4.text-wrapper {{ $t('changing_error_in_bundles') }}
          v-card-text.p2.primary500--text.px-4
            div.p1.primary500--text.mb-3
              ul
                li(v-for="bundle in errorBundles") {{ bundle }}
            div.p1.primary500--text {{ $t('please_remove_bundles') }}
          v-card-actions
            v-spacer
            v-btn.mr-2(color='info500' depressed height=32 @click="openWarningDialog = false" :loading="isLoading" dark)
              .button-AA-small.basic100--text {{ $t('got_it') }}

</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import AddProduct from '@/components/dialogs/AddProduct.vue'
import DuplicateProduct from '@/components/dialogs/DuplicateProduct.vue'
import ShowMoreText from '@/components/ShowMoreText.vue'

export default {
  name: 'ProductItem',
  props: ['parentProduct', 'type', 'setweight', 'setdimension', 'selectAll'],
  components: { AddProduct, DuplicateProduct, ShowMoreText },
  data: () => ({
    showAddProductDialog: false,
    showDuplicateProductDialog: false,
    openDeleteDialog: false,
    openWarningDialog: false,
    isLoading: false,
    errorBundles: [],
    switchKey: 0
  }),
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany'
    }),
    product: {
      get() {
        return this.parentProduct
      },
      set(newValue) {
        this.$emit('edited', newValue)
      }
    },
    status: {
      set(val) {
        this.updateProductStatus(val)
      },
      get() {
        if (this.product.status === 'published') {
          return true
        } else {
          return false
        }
      }
    }
  },
  watch: {
    selectAll (val) {
      this.$set(this.product, 'selected', val)
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    getProductCount() {
      if (this.product.inventory_qty > 99) {
        return '99+'
      }
      return this.product.inventory_qty.toString()
    },
    clickEdit() {
      if (this.member.role !== 'Admin') {
        return
      }
      this.showAddProductDialog = true
    },
    clickDelete() {
      this.openDeleteDialog = true
    },
    startDelete() {
      this.isLoading = true
      this.axios
        .patch(`/products/${this.product.id}/`, { deleted: true })
        .then(() => {
          this.$emit('deleted', this.product)
          this.openDeleteDialog = false
        })
        .catch(error => {
          // Handling bundle error
          if (error.response) {
            const { data } = error.response
            if (data.code && data.code === 'BUNDLE_EXCEPTION') {
              this.errorBundles = data.bundles
              this.openWarningDialog = true
              this.openDeleteDialog = false
              return
            }
          }
          // Everything else
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    updateProductStatus(val) {
      let status = ''
      let word = ''
      if (val) {
        status = 'published'
        word = 'Product published successfully.'
      } else {
        status = 'draft'
        word = 'Product changed to draft'
      }
      this.$axios
        .patch(`/products/${this.product.id}/`, { status: status })
        .then(() => {
          this.showMessage(['success', word])
          // console.log(resp.data)
          this.$emit('status', status)
        })
        .catch(error => {
          // Handling bundle error
          if (error.response) {
            const { data } = error.response
            if (data.code && data.code === 'BUNDLE_EXCEPTION') {
              this.errorBundles = data.bundles
              this.openWarningDialog = true
              this.product.status = 'published'
              this.switchKey++
              return
            }
          }
          // Everything else
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    }
  }
}
</script>

<style scoped>
/* .count-layout {
  position: absolute;
  right: 10px;
  top: 6px;
} */

.product-description {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>

<i18n>
{
  "en": {
    "changing_error_in_bundles": "Changing this product to draft will cause an error in the following bundles:",
    "please_remove_bundles": "Please remove this product from the above bundles before you delete this product." 
  },
  "id": {
    "changing_error_in_bundles": "Mengubah produk ini menjadi draf akan menyebabkan kesalahan dalam Bundle berikut:",
    "please_remove_bundles": "Sila hapus produk ini dari Bundle di atas sebelum kamu menghapus produk ini."
  }
}
</i18n>